import { Button, Card, CardActions, CardContent, Chip, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { EmptyState, ErrorMessage } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Lesson, Mastery } from "services";
import useErrorCatcher from "utils/useErrorCatcher";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  label: {
    fontSize: 18
  },
  section: {
    '& + &': {
      marginTop: theme.spacing(5)
    }
  },
  chip: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

let mounted = false;

const MasterySet = props => {
  const { open, onClose, className, reload, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.lesson);
  const [options, setOptions] = useState([]);
  const [masteries, setMasteries] = useState(model.mastery_levels);

  useEffect(() => {
    mounted = true;
    runAsyncBlock(async () => {
      const { models } = await Mastery.list({ query: { limit: 9999 } });
      mounted && setOptions(models);
      return () => { mounted = false };
    }).finally(() => mounted && setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mounted) {
      setMasteries(model.mastery_levels);
      setErrorMessage("");
    }
  }, [open]); // eslint-disable-line

  if (!open) {
    return null;
  }

  const handleClose = event => {
    event.preventDefault();

    mounted && setLoading(true);
    runAsyncBlock(async () => {
      await Lesson.set_masteries(model.id, { mastery_levels: masteries.map(mastery => mastery.id) });
      mounted && setLoading(false);
      reload();
    }).finally(() => {
      onClose();
    });
  }

  const handleMasteryChange = event => {
    event.persist();
    let id = event.target.value;
    if (!masteries.find(mastery => mastery.id === parseInt(id))) {
      let mastery = options.find(mastery => mastery.id === parseInt(id))
      if (mastery) {
        mounted && setMasteries([...masteries, mastery])
      }
    }
  };

  const deleteMastery = (id) => {
    let newMasteries = [...masteries];
    let index = newMasteries.findIndex(mastery => mastery.id === id);
    if (index >= 0) {
      newMasteries.splice(index, 1);
      mounted && setMasteries(newMasteries);
    }
  }

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Set Assigned Masteries
            </Typography>
            <div className={classes.section}>
              <Card>
                <CardContent className={classes.content}>
                  {masteries.map(mastery => (
                    <Chip
                      key={mastery.id}
                      className={classes.chip}
                      label={mastery.name_en}
                      onDelete={() => deleteMastery(mastery.id)}
                    />
                  ))}
                  <EmptyState active={!masteries.length} message="No masteries assigned." />
                </CardContent>
              </Card>
            </div>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Add Mastery"
                  name="mastery"
                  onChange={handleMasteryChange}
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={""}
                  variant="outlined"
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                  select
                >
                  <option disabled value={""} label="Select Mastery" />
                  {options && options.length > 0 && options.map((mastery, index) => (
                    <option key={index} value={mastery.id} >{mastery.name_en}{mastery.name_zh}</option>
                  ))}
                </TextField>
              </Grid>
              {/* <Grid item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Add Module"
                  name="selected"
                  onChange={handleChange}
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={""}
                  variant="outlined"
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                  select
                  disabled={!mastery}
                >
                  <option disabled value={""} label="Select Module" />
                  {options && options.length > 0 && options.map((opt, index) => (
                    <option key={index} value={opt.id} disabled={masteries.find(mastery => mastery.id === opt.id)} >{opt.name_en}</option>
                  ))}
                </TextField>
              </Grid> */}
              <ErrorMessage message={errorMessage} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Save</Button>
          </CardActions>
        </form>
      </Card>
    </Modal >
  );
};

MasterySet.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

MasterySet.defaultProps = {
  open: false,
  onClose: () => { }
};

export default MasterySet;
